import { arbZgAdjoiningRestLimits } from "../../../domain/adjoiningRest";
import type { AdjoiningRestLimits } from "../../../domain/adjoiningRest";
import { arbZgBreakParameters, youthBreakParameters } from "../../../domain/breakRule";
import type { BreakRuleParameters } from "../../../domain/breakRule";
import type { LocalDate } from "../../../domain/time";

/** Statutory limits the clock screen warns about (spec 7.3.1–7.3.3, 7.3.6). */
export interface ClockLimits {
  readonly break: BreakRuleParameters;
  /** Regular daily maximum (above: compensation needed). */
  readonly dailyRegularMaxSeconds: number;
  /** Absolute daily maximum (above: violation). */
  readonly dailyAbsoluteMaxSeconds: number;
  /** Minimum uninterrupted rest after the end of a working day. */
  readonly minRestSeconds: number;
  /** Rest next to a free Sunday or public holiday (§ 11 (4) ArbZG, F-53); minors with the twelve hours of § 13 JArbSchG. */
  readonly adjoiningRest: AdjoiningRestLimits;
}

/** Limits of `DE-ArbZG-current`. */
export const adultLimits: ClockLimits = {
  break: arbZgBreakParameters,
  dailyRegularMaxSeconds: 28_800,
  dailyAbsoluteMaxSeconds: 36_000,
  minRestSeconds: 39_600,
  adjoiningRest: arbZgAdjoiningRestLimits,
};

/** Limits of `DE-JArbSchG` (stricter in every value). */
export const minorLimits: ClockLimits = {
  break: youthBreakParameters,
  dailyRegularMaxSeconds: 28_800,
  dailyAbsoluteMaxSeconds: 30_600,
  minRestSeconds: 43_200,
  adjoiningRest: { ...arbZgAdjoiningRestLimits, adjoiningRestSeconds: 43_200 },
};

/**
 * Selects the limits for a day. Maternity protection is not known to the client (health data,
 * spec 3.4); the server evaluates it and reports findings after synchronization.
 *
 * @param minorUntil - Eighteenth birthday of a minor or `null`.
 * @param date - The local date.
 * @returns The limits.
 */
export function limitsFor(minorUntil: LocalDate | null, date: LocalDate): ClockLimits {
  const limits = minorUntil !== null && date < minorUntil ? minorLimits : adultLimits;

  return limits;
}
